import { useJxtRouter, useStore } from '@jooxter/core';
import { CookieService } from '@jooxter/storage';
import { JSX, useEffect } from 'react';
import { REDIRECT_AFTER_LOGIN } from './constants';

interface GuardedRouteProps {
  /**
   * Permission check for route
   * @default false
   */
  isRouteAccessible?: boolean;
  /**
   * Route to be redirected to
   * @default '/'
   */
  redirectRoute?: string;
}

/**
 * Component for guarding restricted routes
 *
 * @example Default usage
 * ```ts
 * <GuardedRoute
 *	 isRouteAccessible={true}
 * />
 * ```
 *
 * @example Usage with custom redirected route
 * ```ts
 * <GuardedRoute
 *	 isRouteAccessible={false}
 *	 redirectRoute={'/login'}
 * />
 * ```
 */
export const GuardedRoute = ({ isRouteAccessible = false, redirectRoute = '/' }: GuardedRouteProps): JSX.Element => {
  const { location, navigate, Outlet, Navigate } = useJxtRouter();
  const token = useStore((state) => state.token);

  useEffect(() => {
    if (!token && !isRouteAccessible && location?.pathname) {
      CookieService.setCookie(REDIRECT_AFTER_LOGIN, location.pathname);
    }
    if (token) {
      const redirectUrl = CookieService.getCookie(REDIRECT_AFTER_LOGIN);
      if (redirectUrl) {
        CookieService.deleteCookie(REDIRECT_AFTER_LOGIN);
        navigate(redirectUrl);
      }
    }
  }, []);

  if (!(Outlet && Navigate)) {
    return <></>;
  }

  return isRouteAccessible ? <Outlet /> : <Navigate to={redirectRoute} replace />;
};
